import {
  AxiosException,
  ChatMessageDto,
  ChatRoomDto,
  CreateChatRoomDto,
  CursorBasedPaginated,
  SendMessageDto,
  UpdateApiProps,
} from '@orcar/common';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import {
  createMobiTalkChat,
  createReservation,
  getMobiTalkMessages,
  sendMobiTalkMessage,
} from '@/apis/resources/mobi-talk.api';

export const MOBI_TALK_QUERY_KEYS = {
  MESSAGES: 'mobi-talk-messages',
};

export const useGetMobiTalkMessages = ({
  roomId,
  limit,
}: {
  roomId: number;
  limit: number;
}) => {
  return useInfiniteQuery<
    CursorBasedPaginated<ChatMessageDto>,
    AxiosError<AxiosException>,
    InfiniteData<CursorBasedPaginated<ChatMessageDto>>,
    [string, number],
    string | undefined
  >({
    queryKey: [MOBI_TALK_QUERY_KEYS.MESSAGES, roomId],
    queryFn: ({ pageParam = undefined }) =>
      getMobiTalkMessages(roomId, {
        cursor: pageParam,
        limit,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage.nextCursor ? lastPage.nextCursor : undefined,
  });
};

export const useCreateMobiTalkChat = () => {
  return useMutation<
    ChatRoomDto,
    AxiosError<AxiosException>,
    CreateChatRoomDto
  >({
    mutationFn: createMobiTalkChat,
  });
};

export const useSendMobiTalkMessage = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ChatMessageDto,
    AxiosError<AxiosException>,
    UpdateApiProps<SendMessageDto>
  >({
    mutationFn: sendMobiTalkMessage,
    onSuccess: async (data: ChatMessageDto) => {
      queryClient.setQueryData(
        [MOBI_TALK_QUERY_KEYS.MESSAGES, data.chatRoomId],
        (oldData: InfiniteData<CursorBasedPaginated<ChatMessageDto>>) => {
          if (!oldData) return oldData;

          const newPages = [...oldData.pages];

          if (newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              data: [data, ...newPages[0].data],
            };
          }

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    },
  });
};

export const useCreateReservation = () => {
  return useMutation<
    void,
    AxiosError<AxiosException>,
    {
      id: number;
    }
  >({
    mutationFn: createReservation,
  });
};
